import { cva } from 'class-variance-authority';
import type { PillColorsEnum } from '../types';

const labelVariants = cva('flex', {
  variants: {
    variant: {
      // platform dictates pill label weight: non-solid → regular, solid → medium.
      // is-platform: excludes interface canvases and its specificity (0,2,0) beats
      // Typography's weight-prop utility (0,1,0), so it overrides any per-instance
      // weight on platform regardless of what the consumer passes.
      soft: 'is-platform:font-regular',
      clear: 'is-platform:font-regular',
      solid: 'text-white is-platform:font-medium',
      subtle: 'is-platform:font-regular',
      ghost: 'is-platform:font-regular',
      outlined: 'is-platform:font-regular',
    },
    color: {
      gray: '',
      brand: '',
      error: '',
      warning: '',
      success: '',
      blue: '',
      purple: '',
      fuchsia: '',
      cyan: '',
      lime: '',
      pink: '',
      violet: '',
      indigo: '',
      rose: '',
      yellow: '',
      blueDark: '',
    } satisfies Record<PillColorsEnum, string>,
    disabled: {
      false: '',
    },
  },
  compoundVariants: [
    {
      variant: 'soft',
      color: 'gray',
      className: 'text-utility-gray-700',
    },
    {
      variant: 'soft',
      color: 'brand',
      className: 'text-utility-brand-700 is-platform:text-[var(--palette-brand-700)]',
    },
    {
      variant: 'soft',
      color: 'error',
      className: 'text-utility-error-700',
    },
    {
      variant: 'soft',
      color: 'warning',
      className: 'text-utility-warning-700',
    },
    {
      variant: 'soft',
      color: 'success',
      className: 'text-utility-success-700 is-platform:text-utility-success-600',
    },
    {
      variant: 'soft',
      color: 'blue',
      className: 'text-utility-blue-700 is-platform:text-utility-blue-500',
    },
    {
      variant: 'soft',
      color: 'purple',
      className: 'text-utility-purple-700 is-platform:text-utility-purple-500',
    },
    {
      variant: 'soft',
      color: 'fuchsia',
      className: 'text-utility-fuchsia-700 is-platform:text-utility-fuchsia-500',
    },
    {
      variant: 'soft',
      color: 'cyan',
      className: 'text-utility-cyan-700 is-platform:text-utility-cyan-500',
    },
    {
      variant: 'soft',
      color: 'lime',
      className: 'text-utility-lime-700',
    },
    {
      variant: 'soft',
      color: 'pink',
      className: 'text-utility-pink-700 is-platform:text-utility-pink-500',
    },
    {
      variant: 'soft',
      color: 'violet',
      className: 'text-utility-violet-700 is-platform:text-utility-violet-500',
    },
    {
      variant: 'soft',
      color: 'indigo',
      className: 'text-utility-indigo-700 is-platform:text-utility-indigo-500',
    },
    {
      variant: 'soft',
      color: 'blueDark',
      className: 'text-utility-blue-dark-700',
    },
    {
      variant: 'soft',
      color: 'rose',
      className: 'text-utility-rose-700 is-platform:text-utility-rose-500',
    },
    {
      variant: 'clear',
      color: 'brand',
      className: 'text-utility-brand-700',
    },
    {
      variant: 'clear',
      color: 'gray',
      className: 'text-utility-gray-700',
    },
    {
      variant: 'clear',
      color: 'error',
      className: 'text-utility-error-700',
    },
    {
      variant: 'clear',
      color: 'warning',
      className: 'text-utility-warning-700',
    },
    {
      variant: 'clear',
      color: 'success',
      className: 'text-utility-success-700',
    },
    {
      variant: 'clear',
      color: 'blue',
      className: 'text-utility-blue-700',
    },
    {
      variant: 'clear',
      color: 'purple',
      className: 'text-utility-purple-700',
    },
    {
      variant: 'clear',
      color: 'fuchsia',
      className: 'text-utility-fuchsia-700',
    },
    {
      variant: 'clear',
      color: 'cyan',
      className: 'text-utility-cyan-700 is-platform:text-utility-cyan-500',
    },
    {
      variant: 'clear',
      color: 'lime',
      className: 'text-utility-lime-700',
    },
    {
      variant: 'clear',
      color: 'pink',
      className: 'text-utility-pink-700',
    },
    {
      variant: 'clear',
      color: 'violet',
      className: 'text-utility-violet-700',
    },
    {
      variant: 'clear',
      color: 'indigo',
      className: 'text-utility-indigo-700',
    },
    {
      variant: 'clear',
      color: 'rose',
      className: 'text-utility-rose-700',
    },
    {
      variant: 'clear',
      color: 'blueDark',
      className: 'text-utility-blue-dark-700',
    },
    {
      variant: 'subtle',
      color: 'gray',
      className: 'text-utility-gray-700',
    },
    {
      variant: 'subtle',
      color: 'brand',
      className: 'text-utility-brand-700',
    },
    {
      variant: 'subtle',
      color: 'error',
      className: 'text-utility-error-700',
    },
    {
      variant: 'subtle',
      color: 'warning',
      className: 'text-utility-warning-700',
    },
    {
      variant: 'subtle',
      color: 'success',
      className: 'text-utility-success-700',
    },
    {
      variant: 'subtle',
      color: 'blue',
      className: 'text-utility-blue-700',
    },
    {
      variant: 'subtle',
      color: 'purple',
      className: 'text-utility-purple-700',
    },
    {
      variant: 'subtle',
      color: 'fuchsia',
      className: 'text-utility-fuchsia-700',
    },
    {
      variant: 'subtle',
      color: 'cyan',
      className: 'text-utility-cyan-700',
    },
    {
      variant: 'subtle',
      color: 'lime',
      className: 'text-utility-lime-700',
    },
    {
      variant: 'subtle',
      color: 'pink',
      className: 'text-utility-pink-700',
    },
    {
      variant: 'subtle',
      color: 'violet',
      className: 'text-utility-violet-700',
    },
    {
      variant: 'subtle',
      color: 'indigo',
      className: 'text-utility-indigo-700',
    },
    {
      variant: 'subtle',
      color: 'rose',
      className: 'text-utility-rose-700',
    },
    {
      variant: 'subtle',
      color: 'blueDark',
      className: 'text-utility-blue-dark-700',
    },
    {
      variant: 'ghost',
      color: 'brand',
      className: 'text-utility-brand-700',
    },
    {
      variant: 'ghost',
      color: 'gray',
      className: 'text-utility-gray-700',
    },
    {
      variant: 'ghost',
      color: 'error',
      className: 'text-utility-error-700',
    },
    {
      variant: 'ghost',
      color: 'warning',
      className: 'text-utility-warning-700',
    },
    {
      variant: 'ghost',
      color: 'success',
      className: 'text-utility-success-700',
    },
    {
      variant: 'ghost',
      color: 'blue',
      className: 'text-utility-blue-700',
    },
    {
      variant: 'ghost',
      color: 'purple',
      className: 'text-utility-purple-700',
    },
    {
      variant: 'ghost',
      color: 'fuchsia',
      className: 'text-utility-fuchsia-700',
    },
    {
      variant: 'ghost',
      color: 'cyan',
      className: 'text-utility-cyan-700',
    },
    {
      variant: 'ghost',
      color: 'lime',
      className: 'text-utility-lime-700',
    },
    {
      variant: 'ghost',
      color: 'pink',
      className: 'text-utility-pink-700',
    },
    {
      variant: 'ghost',
      color: 'violet',
      className: 'text-utility-violet-700',
    },
    {
      variant: 'ghost',
      color: 'indigo',
      className: 'text-utility-indigo-700',
    },
    {
      variant: 'ghost',
      color: 'rose',
      className: 'text-utility-rose-700',
    },
    {
      variant: 'ghost',
      color: 'blueDark',
      className: 'text-utility-blue-dark-700',
    },
    {
      variant: 'outlined',
      color: 'gray',
      className: 'text-utility-gray-700',
    },
    {
      variant: 'outlined',
      color: 'brand',
      className: 'text-utility-brand-700',
    },
    {
      variant: 'outlined',
      color: 'error',
      className: 'text-utility-error-700',
    },
    {
      variant: 'outlined',
      color: 'warning',
      className: 'text-utility-warning-700',
    },
    {
      variant: 'outlined',
      color: 'success',
      className: 'text-utility-success-700',
    },
    {
      variant: 'outlined',
      color: 'blue',
      className: 'text-utility-blue-700',
    },
    {
      variant: 'outlined',
      color: 'purple',
      className: 'text-utility-purple-700',
    },
    {
      variant: 'outlined',
      color: 'fuchsia',
      className: 'text-utility-fuchsia-700',
    },
    {
      variant: 'outlined',
      color: 'cyan',
      className: 'text-utility-cyan-700',
    },
    {
      variant: 'outlined',
      color: 'lime',
      className: 'text-utility-lime-700',
    },
    {
      variant: 'outlined',
      color: 'pink',
      className: 'text-utility-pink-700',
    },
    {
      variant: 'outlined',
      color: 'violet',
      className: 'text-utility-violet-700',
    },
    {
      variant: 'outlined',
      color: 'indigo',
      className: 'text-utility-indigo-700',
    },
    {
      variant: 'outlined',
      color: 'rose',
      className: 'text-utility-rose-700',
    },
    {
      variant: 'outlined',
      color: 'yellow',
      className: 'text-utility-warning-300',
    },
    {
      variant: 'outlined',
      color: 'blueDark',
      className: 'text-utility-blue-dark-700',
    },
  ],
});

export default labelVariants;
